import { useAuthStore } from "@/store/auth";
import { useUserInfo, useRefetchUserInfo } from "./useUserInfo";

/**
 * useEffectiveUser —— 获取当前用户信息的自定义 Hook
 *
 * 设计理念：
 * - 任何组件只需一行代码即可获取当前用户信息。
 * - 兼顾“快速响应”（优先返回本地缓存用户）和“后台刷新”（自动获取最新用户信息）。
 * - 适用于需要用户信息的所有场景，无需关心缓存/刷新细节。
 *
 * 实现细节：
 * - 优先返回本地缓存的用户（cachedUser），保证 UI 响应迅速。
 * - 后台自动请求最新用户信息（freshUser），获取后自动更新。
 * - 提供 isLoading、isError 等状态，便于组件做加载/错误处理。
 * - 提供 refetchUserInfo 方法，可手动触发用户信息刷新。
 *
 * 返回值说明：
 * - user：当前可用的用户对象（优先 freshUser，否则 cachedUser）
 * - isLoading：是否正在加载用户信息
 * - isError：用户信息请求是否出错
 * - freshUser：最新拉取到的用户对象
 * - cachedUser：本地缓存的用户对象
 * - refetchUserInfo：手动刷新用户信息的方法
 */
export function useEffectiveUser() {
  // 从全局状态管理中获取本地缓存的用户对象，优点是响应快
  const cachedUser = useAuthStore((s) => s.user);
  // 通过 useUserInfo 获取远程拉取的最新用户信息及其加载/错误状态
  const { data: freshUser, isLoading, isError } = useUserInfo();
  // 提供手动刷新用户信息的方法
  const refetchUserInfo = useRefetchUserInfo();

  return {
    // 一开始 freshUser 为空，先cachedUser；
    // 当 freshUser 获取成功后，react 会自动更新 user 的值
    // 这是 react hook 的自动更新机制，不需要手动更新
    user: freshUser || cachedUser,
    isLoading,
    isError,
    freshUser,
    cachedUser,
    refetchUserInfo,
  };
}
